import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import { BsArrowLeft } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import axios from 'axios'
import Cookies from 'js-cookie'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import {
  fetchAnnouncementById,
  getAnnouncementErrorMessage,
  parseAnnouncementOne,
} from './commonComponents/announcementApi'
import { BASE_URL } from './commonComponents/ApiEndpoints'

const formatDateTime = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusBadge = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="dark">Archived</Badge>
  return <Badge bg="light" text="dark">{s || '—'}</Badge>
}

const priorityBadge = (p) => {
  const v = String(p || '').toLowerCase()
  if (v === 'high') return <Badge bg="danger">Priority: High</Badge>
  if (v === 'mid') return <Badge bg="warning" text="dark">Priority: Mid</Badge>
  if (v === 'low') return <Badge bg="secondary">Priority: Low</Badge>
  return <Badge bg="light" text="dark">Priority: —</Badge>
}

const normalizeIdList = (raw, keys = []) => {
  if (!Array.isArray(raw)) return []
  return raw
    .map((x) => {
      if (x && typeof x === 'object') {
        for (const k of keys) {
          if (x[k] != null && x[k] !== '') return x[k]
        }
        return x.id ?? x.value ?? null
      }
      return x
    })
    .filter((x) => x != null && x !== '')
}

const getMessageSections = (rawMessage) => {
  const lines = String(rawMessage || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

  const sections = []
  let current = null

  const startsSection = (line) => {
    if (/^title:/i.test(line) || /^message:/i.test(line)) return false
    // Emoji-led headers or bullet-like lead lines
    return /^[^\w\s]/.test(line)
  }

  lines.forEach((line) => {
    if (startsSection(line)) {
      if (current) sections.push(current)
      current = { heading: line, title: '', message: '', extras: [] }
      return
    }

    if (!current) current = { heading: '', title: '', message: '', extras: [] }

    if (/^title:/i.test(line)) {
      current.title = line.replace(/^title:\s*/i, '')
      return
    }

    if (/^message:/i.test(line)) {
      current.message = line.replace(/^message:\s*/i, '')
      return
    }

    if (!current.message && !current.title) {
      current.message = line
    } else {
      current.extras.push(line)
    }
  })

  if (current) sections.push(current)
  return sections.length
    ? sections
    : [{ heading: '', title: '', message: String(rawMessage || '—'), extras: [] }]
}

const isPdfLink = (value) => /\.pdf(\?|$)/i.test(String(value || ''))
const isImageLink = (value) => /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(String(value || ''))

export default function ViewAnnouncement() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name: instituteName } = useInstitute()

  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isTeacher =
    normalizedUserType === 'teacher' || normalizedRoles.includes('teacher')

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [item, setItem] = useState(null)
  const [classesList, setClassesList] = useState([])
  const [coursesList, setCoursesList] = useState([])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    if (!id) {
      setLoading(false)
      toast.error('Invalid announcement ID.')
      return
    }

    const load = async () => {
      setLoading(true)
      try {
        const res = await fetchAnnouncementById(id)
        const row = parseAnnouncementOne(res)
        setItem(row)
        if (!row) {
          toast.error('Announcement not found.')
        }
      } catch (err) {
        const msg = getAnnouncementErrorMessage(err, navigate)
        toast.error(msg)
        setItem(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, navigate])

  useEffect(() => {
    const loadClassesAndCourses = async () => {
      try {
        const instituteId = Cookies.get('institute_id')
        const headers = {
          'institute-Id': Number(instituteId),
          Authorization: `Bearer ${user?.token}`,
        }
        const [classRes, courseRes] = await Promise.all([
          axios.get(`${BASE_URL}/class/all`, { headers }),
          axios.get(`${BASE_URL}/course/all`, { headers }),
        ])
        setClassesList(classRes?.data?.data || [])
        setCoursesList(courseRes?.data?.data || [])
        console.log("classRes?.data?.data",classRes?.data?.data)
      } catch {
        setClassesList([])
        setCoursesList([])
      }
    }
    loadClassesAndCourses()
  }, [])

  const title = item?.title || '—'
  const message = item?.message ?? item?.description ?? '—'
  const scope = item?.scope ?? '—'
  const status = item?.status
  const priority = item?.priority
  const mediaLink = item?.media_link || ''
  const messageSections = getMessageSections(message)
  const classIds = normalizeIdList(item?.class_ids, ['class_id', 'id'])
  const courseIds = normalizeIdList(item?.course_ids, ['course_id', 'id'])
  const getClassName = (idValue) => {
    const key = String(idValue)
    const matched = classesList.find((c) => String(c?.id ?? c?.class_id) === key)
    return matched?.class_name || matched?.name || idValue
  }
  const getCourseName = (idValue) => {
    const key = String(idValue)
    const matched = coursesList.find((c) => String(c?.id ?? c?.course_id) === key)
    return matched?.name || matched?.course_name || idValue
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .announcement-view-root {
            background: #f9fafb;
            border-radius: 16px;
            transition: box-shadow 0.2s ease, transform 0.2s ease;
          }
          .announcement-view-root:hover {
            box-shadow: 0 10px 26px rgba(15, 23, 42, 0.10);
            transform: translateY(-1px);
          }
          .meta-label {
            font-size: 12px;
            color: #6b7280;
            font-weight: 600;
            margin-bottom: 4px;
          }
          .soft-box {
            background: #f3f4f6;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 14px;
          }
          .section-divider {
            border-top: 1px solid #e5e7eb;
            margin: 20px 0;
          }
          @media (max-width: 768px) {
            .announcement-card-body {
              padding: 16px !important;
            }
            .announcement-title {
              font-size: 18px !important;
            }
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    View Announcement
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    {instituteName ? `${instituteName} · ` : ''}
                    Details
                  </p>
                </div>
              </div>

              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: isMobile ? '900px' : '1080px',
          }}
        >
          {!isTeacher && (
            <div className={`mb-3 ${isMobile ? '' : 'text-end'}`}>
              <Button
                variant="light"
                className={`border d-inline-flex align-items-center justify-content-center gap-2 ${isMobile ? 'w-100' : ''}`}
                style={{ borderRadius: '10px', minHeight: '42px' }}
                onClick={() => navigate('/announcements')}
              >
                <BsArrowLeft /> Back to list
              </Button>
            </div>
          )}

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status" variant="primary" />
            </div>
          ) : !item ? (
            <Card className="border" style={{ borderRadius: '12px' }}>
              <Card.Body className="text-center text-muted py-5">
                Could not load this announcement.
              </Card.Body>
            </Card>
          ) : (
            <Card
              className="border shadow-sm announcement-view-root"
              style={{ borderRadius: '16px', overflow: 'hidden' }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #223843 0%, #1f323a 100%)',
                  padding: isMobile ? '16px' : '20px 24px',
                }}
              >
                <h4
                  className="text-white m-0 fw-bold announcement-title"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: isMobile ? '20px' : '26px' }}
                >
                  {title}
                </h4>
                <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                  {statusBadge(status)}
                  <Badge bg="light" text="dark" style={{ textTransform: 'capitalize' }}>
                    Scope: {scope}
                  </Badge>
                  {priorityBadge(priority)}
                </div>
              </div>
              <Card.Body style={{ padding: '24px' }} className="announcement-card-body">
                <Row className="g-3 mb-4">
                  <Col xs={12} md={6}>
                    <div className="soft-box h-100">
                      <div className="meta-label">Publish at</div>
                      <div style={{ fontSize: isMobile ? '14px' : '15px', color: '#111827', fontWeight: 500 }}>
                        {formatDateTime(item.publish_at)}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="soft-box h-100">
                      <div className="meta-label">Expire at</div>
                      <div style={{ fontSize: isMobile ? '14px' : '15px', color: '#111827', fontWeight: 500 }}>
                        {formatDateTime(item.expire_at)}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="section-divider" />

                <div className="mb-4">
                  <div className="meta-label" style={{ marginBottom: '10px' }}>Message</div>
                  <div className="soft-box text-start">
                    {messageSections.map((section, idx) => (
                      <div key={`${section.heading}-${idx}`} style={{ marginBottom: idx === messageSections.length - 1 ? 0 : '14px' }}>
                        {section.heading ? (
                          <div style={{ fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
                            {section.heading}
                          </div>
                        ) : null}
                        {section.title ? (
                          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                            Title: {section.title}
                          </div>
                        ) : null}
                        {section.message ? (
                          <div style={{ color: '#111827', lineHeight: 1.55, marginBottom: section.extras.length ? '4px' : 0 }}>
                            Message: {section.message}
                          </div>
                        ) : null}
                        {section.extras.length > 0 ? (
                          <div style={{ color: '#374151', lineHeight: 1.5 }}>
                            {section.extras.join(' ')}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                {mediaLink ? (
                  <>
                    <div className="section-divider" />
                    <div className="mb-4">
                      <div className="meta-label" style={{ marginBottom: '10px' }}>Media</div>
                      <div className="soft-box">
                        {isImageLink(mediaLink) ? (
                          <div>
                            <img
                              src={mediaLink}
                              alt="Announcement media"
                              style={{
                                width: '100%',
                                maxHeight: isMobile ? '220px' : '320px',
                                objectFit: 'contain',
                                borderRadius: '10px',
                                border: '1px solid #d1d5db',
                                background: '#fff',
                              }}
                            />
                            <div className="mt-2">
                              <a href={mediaLink} target="_blank" rel="noreferrer">
                                Preview image
                              </a>
                            </div>
                          </div>
                        ) : isPdfLink(mediaLink) ? (
                          <Button
                            as="a"
                            href={mediaLink}
                            target="_blank"
                            rel="noreferrer"
                            variant="outline-primary"
                            size="sm"
                          >
                            View PDF
                          </Button>
                        ) : (
                          <a href={mediaLink} target="_blank" rel="noreferrer">
                            Open attachment
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}

                {classIds.length > 0 || courseIds.length > 0 ? (
                  <>
                    <div className="section-divider" />
                    <div className="mt-1">
                      {classIds.length > 0 ? (
                        <div className="mb-3">
                          <div className="meta-label" style={{ marginBottom: '8px' }}>Class Name</div>
                          <div className="d-flex flex-wrap gap-2">
                            {classIds.map((cid, idx) => (
                              <Badge key={`class-${cid}-${idx}`} bg="light" text="dark" className="border">
                                {getClassName(cid)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {courseIds.length > 0 ? (
                        <div>
                          <div className="meta-label" style={{ marginBottom: '8px' }}>Course Name</div>
                          <div className="d-flex flex-wrap gap-2">
                            {courseIds.map((cid, idx) => (
                              <Badge key={`course-${cid}-${idx}`} bg="light" text="dark" className="border">
                                {getCourseName(cid)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
    </>
  )
}
